import { setPage, updatePaginationData } from '@ngneat/elf-pagination';
import { AxiosError, AxiosResponse } from 'axios';
import { Observable, catchError, from, map, switchMap, tap } from 'rxjs';

import APIAxios, { APIRoutes } from '@api/axios.api';
import SnackError from '@utils/error.utils';

import { GridSortItem } from '@mui/x-data-grid-pro';
import { UIEntitiesRef, updateAllEntities, upsertEntities, upsertEntitiesById } from '@ngneat/elf-entities';
import { auditDetailsService } from '@store/audits/audit-details/audits-details.service';
import { Pagination } from '@store/common/pagination.model';
import { TableFilter } from '@store/common/tableFilter.model';
import { Audit, AuditsFilters, AuditsPagination, CreateAuditDto, MergeAuditDto, UpdateAuditDto } from './audits.model';
import { getAuditsDataSource } from './audits.requests';
import { auditsStore } from './audits.store';
import {auditsQuery} from '@store/audits/audits.query';

export class AuditsService {
  store = auditsStore;

  setSort = (sort: GridSortItem) => this.store.update((state) => ({ ...state, sort }));
  setPagination = (name: string) => (value: number) =>
    this.store.update((state) => ({
      ...state,
      tablePagination: {
        ...state.tablePagination,
        [name]: value,
      },
    }));

  resetFilters = () =>
    this.store.update((state) => ({
      ...state,
      filters: { search: '', searchMerge: '', tableFilters: AuditsFilters.initTableFilters() },
      tablePagination: {
        ...state.tablePagination,
        page: 1
      }
    }));

  setFilters = (name: string) => (value: any) =>
    this.store.update((state) => ({
      ...state,
      filters: {
        ...state.filters,
        [name]: value,
      },
      tablePagination: {
        ...state.tablePagination,
        page: 1
      },
    }));

  setAudits = (audits: Audit[]) => this.store.update((state) => ({
      ...state,
      auditsMerge: audits
  }));

  getAudits = (): Observable<AuditsPagination> => {
    const {
      filters: { search, status, tableFilters },
      sort,
      tablePagination,
    } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(
      APIAxios({
        ...APIRoutes.GETAudits(),
        params: {
          ...requestFilters,
          searchString: search || undefined,
          status: status || undefined,
          page: tablePagination.page,
          limit: tablePagination.limit,
          sortBy: sort.field,
          orderBy: sort.sort?.toLocaleUpperCase(),
        },
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<AuditsPagination>) => {
        return response.data;
      }),
      tap((auditPagination) => {
        const paginationData = Pagination.toPaginationData(auditPagination, tablePagination.page);

        this.store.update(
          upsertEntities(auditPagination.data),
          upsertEntitiesById(auditPagination.data.map((auditsUnit) => auditsUnit.id), {
            updater: (auditUi) => auditUi,
            creator: (auditId) => ({ id: auditId, selected: false }),
            ref: UIEntitiesRef,
          }),
          updatePaginationData(paginationData),
          setPage(
            paginationData.currentPage,
            auditPagination.data.map((audit) => audit.id)
          ),
          getAuditsDataSource.setSuccess()
        );
      }),
      getAuditsDataSource.trackRequestStatus()

    );
  };

    searchAudits = (searchMerge?: string): Observable<AuditsPagination> => {
        const {
            filters: { tableFilters },
            tablePagination,
        } = this.store.getValue();
        const requestFilters = TableFilter.toRequestFilters(tableFilters);

        return from(
            APIAxios({
                ...APIRoutes.GETAudits(),
                params: {
                    ...requestFilters,
                    searchString: searchMerge || undefined,
                    limit: tablePagination.limit,
                },
            })
        ).pipe(
            catchError((err: AxiosError) => {
                throw new SnackError((err.response as any)?.data?.message, 'error');
            }),
            map((response: AxiosResponse<AuditsPagination>) => {
                return response.data;
            }),
            tap((auditPagination) => {
                this.setAudits(auditPagination.data);
            }),

        );
    };

  createAudit = (data: CreateAuditDto) => {
    return from(APIAxios({
      ...APIRoutes.POSTAudits(),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Audit>) => {
          return response.data;
        }),
        switchMap(() => this.getAudits()),
      );
  };

    updateAudit = (data: UpdateAuditDto, auditId: number | string) => {
        return from(APIAxios({
            ...APIRoutes.PATCHAudits(auditId),
            data,
        }))
            .pipe(
                catchError((err: AxiosError) => {
                    throw new SnackError((err.response as any)?.data?.message, 'error');
                }),
                map((response: AxiosResponse<Audit>) => {
                    return response.data;
                }),
                switchMap(() => this.getAudits()),
                switchMap(() => auditDetailsService.getAuditById(auditId))
            );
    };

  deleteAudit = (auditId: number | string) => {
    return from(APIAxios(APIRoutes.DELETEAudits(auditId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getAudits()),
      );
  };

    saveVersionAudit = (auditId: number | string) => {
        return from(APIAxios(APIRoutes.GETAuditsVersionUpdate(auditId)))
            .pipe(
                catchError((err: AxiosError) => {
                    throw new SnackError((err.response as any)?.data?.message, 'error');
                }),
                switchMap(() => auditDetailsService.getAuditById(auditId))
            );
    };

  mergeAudits = (data: MergeAuditDto) => {
    return from(APIAxios({
      ...APIRoutes.POSTMergeAudit(),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        })
      );
  };

  setSelectedAudits = (supplierIds: number[]) => {
    this.store.update(
      updateAllEntities(
        (auditsUI) => ({ ...auditsUI, selected: supplierIds.includes(auditsUI.id) }),
        { ref: UIEntitiesRef },
      ),
    );
  };

    deleteAudits() {
        const ids = auditsQuery.selectedAudits;

        this.setSelectedAudits([]);
        return from(
            APIAxios({
                ...APIRoutes.DELETESelectedAudits(),
                data: {ids},
            })
        ).pipe(
            catchError((err: AxiosError) => {
                throw new SnackError((err.response as any)?.data?.message, 'error');
            }),
            switchMap(() => this.getAudits().pipe(map(() => ids)))
        );
    }

}

export const auditsService = new AuditsService();
